import React, { FunctionComponent, useState } from 'react';
// import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
// import useMediaQuery from '@mui/material/useMediaQuery';

import { fetch_all, fetch_one } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';

import DeleteIcon from '@mui/icons-material/Delete';

import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
} from '@mui/material';

import {
  Button,
  IconButton,
  Page,
  Select,
  TextInput,
  Typography
} from '../../../components/v2/styled';
import { SnackSaved } from '../../../components/v2/snacks/Saved';

import { NutritionalValue } from '../../../models/NutritionalValue';

type AdhImportFlag = {
  id: number;
  adh_import_id: number;
  adh_profile_id?: number;
  country_id?: number;
  group_name: string;
  nutrient_name: string;
  unit?: string;
  flag_type?: string;
  status?: string;
  row_data?: any;
  resolution?: any;
  raw_value_key?: string;
  adh_profile?: {id: number; name: string};
}

type AdhImport = {
  id?: number;
  status?: string;
  source?: string;
}

type FlagCounts = {
  total: number;
  open: number;
  resolved: number;
  skipped: number;
  no_norm: number;
}

type LinkLineState = {
  nutritional_value_id?: number;
  lower_limit?: string;
  higher_limit?: string;
  maximum?: string;
}

type Props = {}

const FLAG_TYPES = ["nutritional_value_not_found", "unit_mismatch", "unparseable_value", "special_maximum", "profile_metadata", "no_norm"];
const LINK_FLAG_TYPES = ["nutritional_value_not_found", "unit_mismatch", "unparseable_value", "special_maximum"];

function parseNumber(value?: string): number | undefined {
  if (!value || !value.trim()) return undefined;
  const parsed = parseFloat(value.replace(",", "."));
  return isNaN(parsed) ? undefined : parsed;
}

function linksToPayload(lines: LinkLineState[]) {
  return lines
    .filter(fff => !!fff.nutritional_value_id)
    .map(fff => ({
      nutritional_value_id: fff.nutritional_value_id,
      lower_limit: parseNumber(fff.lower_limit),
      higher_limit: parseNumber(fff.higher_limit),
      maximum: parseNumber(fff.maximum),
    }));
}

function nvSuffix(nv?: NutritionalValue): string {
  if (!nv) return "";
  let suffix = "";
  if (!!nv.measure || !!nv.is_grams_per_kilogram) suffix += " (";
  if (!!nv.measure) suffix += nv.measure;
  if (!!nv.is_grams_per_kilogram) suffix += "/kg";
  if (!!nv.measure || !!nv.is_grams_per_kilogram) suffix += ")";
  if (!!nv.is_percentage_energy) suffix += " (% EN)";
  return suffix;
}

function chipColor(status?: string): "warning" | "success" | "default" | "info" {
  if (status === "open") return "warning";
  if (status === "resolved") return "success";
  if (status === "no_norm") return "info";
  return "default";
}

export const AdminFoodCalculationAdhImportReview: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  // const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);
  const params = useParams();

  const [savedOpen, setSavedOpen] = useState<boolean>(false);
  const [countryId, setCountryId] = useState<string>("");
  const [flagType, setFlagType] = useState<string>("");
  const [flagStatus, setFlagStatus] = useState<string>("open");
  const [linkLines, setLinkLines] = useState<{[flagId: number]: LinkLineState[]}>({});
  const [formNotes, setFormNotes] = useState<{[flagId: number]: string}>({});

  const {
    data: importObject,
  } = useQuery({
    queryKey: ["adh_import", login?.id, params.id],
    queryFn: () =>
      fetch_one<AdhImport>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'adh_import',
          id: params.id,
          fields: ['id', 'status', 'source'],
        },
        login
      ),
    enabled: !!login?.id && !!params.id
  });

  const {
    data: countsData,
    refetch: countsRefetch,
  } = useQuery({
    queryKey: ["adh_import", "flag_counts", login?.id, params.id],
    queryFn: () =>
      fetch_one<{custom_result?: FlagCounts}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        {
          object: 'adh_import',
          id: params.id,
          class_action: 'custom_api_flag_counts'
        },
        login
      ),
    enabled: !!login?.id && !!params.id,
    refetchInterval: (query) => ((((query.state.data as {custom_result?: FlagCounts} | undefined)?.custom_result)?.open || 0) > 0 ? 15000 : false)
  });
  const counts = countsData?.custom_result;

  const {
    data: flags,
    isLoading: flagsLoading,
    refetch: flagsRefetch,
  } = useQuery({
    queryKey: ["adh_import", "flags", login?.id, params.id, countryId, flagType, flagStatus],
    queryFn: () =>
      fetch_all<AdhImportFlag>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'adh_import_flag',
          fields: ['id', 'adh_import_id', 'adh_profile_id', 'country_id', 'group_name', 'nutrient_name', 'unit', 'flag_type', 'status', 'row_data', 'resolution', 'raw_value_key', 'adh_profile|id/name'],
          order: "id ASC",
          per_page: 100,
          page: 0,
          filter: {
            search: '',
            advanced: {
              adh_import_id: params.id,
              status: flagStatus,
              ...(!!countryId ? {country_id: countryId} : {}),
              ...(!!flagType ? {flag_type: flagType} : {})
            }
          }
        },
        login
      ),
    enabled: !!login?.id && !!params.id
  });

  const {
    data: nutritionalValues,
  } = useQuery({
    queryKey: ["admin", "all_nutritional_values"],
    queryFn: () =>
      fetch_all<NutritionalValue>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'nutritional_value',
          fields: ['id', 'name', 'has_maximum', 'measure', 'is_percentage_energy', 'is_grams_per_kilogram'],
          order: "name ASC",
          per_page: 1000,
          page: 0,
          filter: {
            search: '',
            advanced: {
              is_admin: 1
            }
          }
        },
        login
      ),
    enabled: !!login?.id
  });

  const mutationResolveFlag = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onError: () => {
      return true;
    },
    onSuccess: () => {
      setSavedOpen(true);
      flagsRefetch();
      countsRefetch();
    },
  });

  function linesFor(flagId: number): LinkLineState[] {
    return linkLines[flagId] || [{nutritional_value_id: undefined}];
  }

  function setLines(flagId: number, lines: LinkLineState[]) {
    setLinkLines({
      ...linkLines,
      [flagId]: lines
    });
  }

  function updateLine(flagId: number, index: number, updates: Partial<LinkLineState>) {
    setLines(flagId, linesFor(flagId).map((line, i) => i === index ? {...line, ...updates} : line));
  }

  function nvById(id?: number): NutritionalValue | undefined {
    return (nutritionalValues || []).find(nv => nv.id === id);
  }

  function identicalOpenCount(flag: AdhImportFlag): number {
    if (!flag.raw_value_key) return 0;
    return (flags || []).filter(fff => fff.status === "open" && fff.raw_value_key === flag.raw_value_key).length;
  }

  function resolveFlag(flag: AdhImportFlag, status: "resolved" | "skipped") {
    mutationResolveFlag.mutate({
      formData: {
        object: 'adh_import_flag',
        class_action: 'custom_api_resolve_flag',
        flag_id: flag.id,
        status: status,
        note: formNotes[flag.id] || '',
        links: status === "skipped" ? [] : linksToPayload(linesFor(flag.id))
      }
    });
  }

  function resolveFlagBulk(flag: AdhImportFlag) {
    mutationResolveFlag.mutate({
      formData: {
        object: 'adh_import_flag',
        class_action: 'custom_api_resolve_flags_bulk',
        raw_value_key: flag.raw_value_key,
        status: 'resolved',
        note: formNotes[flag.id] || '',
        links: linksToPayload(linesFor(flag.id))
      }
    });
  }

  function renderContextLine(label: string, value?: string) {
    if (!value) return null;
    return <Typography variant="caption" sx={{display: 'block', marginLeft: 2}}>{label}: {value}</Typography>;
  }

  function renderLinkLines(flag: AdhImportFlag) {
    const lines = linesFor(flag.id);
    return <Grid container spacing={1} sx={{marginTop: 1}}>
      {lines.map((line, i) => {
        const nv = nvById(line.nutritional_value_id);
        const showMaximum = flag.flag_type === "special_maximum" || !!nv?.has_maximum;
        return <React.Fragment key={i}>
          <Grid item xs={4}>
            <Select
              fullWidth
              displayEmpty
              caption={t("admin.adh_imports.flags.resolve.nutritional_value")}
              id={["flag_nv", flag.id, i].join("_")}
              value={line.nutritional_value_id?.toString() || ''}
              setValue={(v) => {
                updateLine(flag.id, i, {
                  nutritional_value_id: !!v ? parseInt(v.toString(), 10) : undefined
                });
              }}
            >
              <MenuItem value="">------</MenuItem>
              {(nutritionalValues || []).map(nv => <MenuItem key={nv.id} value={nv.id}>{nv.name}{nvSuffix(nv)}</MenuItem>)}
            </Select>
          </Grid>
          <Grid item xs={2}>
            <TextInput
              id={["flag_low", flag.id, i].join("_")}
              caption={t("admin.adh_imports.flags.resolve.lower_limit")}
              value={line.lower_limit || ''}
              onChange={(e) => {updateLine(flag.id, i, {lower_limit: e});}}
              sx={{marginTop: 0}}
            />
          </Grid>
          <Grid item xs={2}>
            <TextInput
              id={["flag_high", flag.id, i].join("_")}
              caption={t("admin.adh_imports.flags.resolve.higher_limit")}
              value={line.higher_limit || ''}
              onChange={(e) => {updateLine(flag.id, i, {higher_limit: e});}}
              sx={{marginTop: 0}}
            />
          </Grid>
          <Grid item xs={2}>
            {showMaximum && <TextInput
              id={["flag_max", flag.id, i].join("_")}
              caption={t("admin.adh_imports.flags.resolve.maximum")}
              value={line.maximum || ''}
              onChange={(e) => {updateLine(flag.id, i, {maximum: e});}}
              sx={{marginTop: 0}}
            />}
          </Grid>
          <Grid item xs={2}>
            {lines.length > 1 && <IconButton
              title={t("admin.adh_imports.flags.resolve.remove_link")}
              color="default"
              onClick={() => {setLines(flag.id, lines.filter((fff, j) => j !== i));}}
            >
              <DeleteIcon />
            </IconButton>}
          </Grid>
        </React.Fragment>;
      })}
    </Grid>;
  }

  function renderOpenFlagForm(flag: AdhImportFlag) {
    const hasLinkForm = LINK_FLAG_TYPES.indexOf(flag.flag_type || '') > -1;
    return <Grid container spacing={1} sx={{marginTop: 1}}>
      {hasLinkForm && <>
        {renderLinkLines(flag)}
        <Grid item xs={12}>
          <Button
            label={t("admin.adh_imports.flags.resolve.add_link")}
            id={["flag_add", flag.id].join("_")}
            text
            onClick={() => {setLines(flag.id, [...linesFor(flag.id), {nutritional_value_id: undefined}]);}}
            sx={{marginTop: 0}}
          />
        </Grid>
      </>}
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="center">
          {hasLinkForm && <Grid item>
            <Button
              label={t("admin.adh_imports.flags.resolve.save")}
              id={["flag_save", flag.id].join("_")}
              contained
              loading={mutationResolveFlag.isPending && mutationResolveFlag.variables?.formData?.flag_id === flag.id}
              onClick={() => {resolveFlag(flag, "resolved");}}
              sx={{marginTop: 0}}
            />
          </Grid>}
          <Grid item>
            <Button
              label={t("admin.adh_imports.flags.resolve.skip")}
              id={["flag_skip", flag.id].join("_")}
              text
              onClick={() => {resolveFlag(flag, "skipped");}}
              sx={{marginTop: 0}}
            />
          </Grid>
          {hasLinkForm && <Grid item xs={4}>
            <TextInput
              id={["flag_note", flag.id].join("_")}
              caption={t("admin.adh_imports.flags.resolve.note")}
              value={formNotes[flag.id] || ''}
              onChange={(e) => {setFormNotes({...formNotes, [flag.id]: e});}}
              sx={{marginTop: 0}}
            />
          </Grid>}
        </Grid>
      </Grid>
      {hasLinkForm && !!flag.raw_value_key && <Grid item xs={12}>
        <Typography variant="caption">{t("admin.adh_imports.flags.resolve.bulk")}</Typography>{' '}
        <Button
          label="BULK"
          id={["flag_bulk", flag.id].join("_")}
          onClick={() => {resolveFlagBulk(flag);}}
          sx={{marginTop: 0}}
        />
        {' '}<Box component="span" sx={{fontSize: '0.75rem'}}>({identicalOpenCount(flag)})</Box>
      </Grid>}
    </Grid>;
  }

  function flagHeader(flag: AdhImportFlag): string {
    const country = flag.country_id === 1 ? "BE" : flag.country_id === 2 ? "NL" : "";
    const nutrient = [flag.nutrient_name, !!flag.unit ? `(${flag.unit})` : ""].filter(fff => !!fff).join(" ");
    return [flag.group_name, country, nutrient, t(["admin.adh_imports.flags.type.", flag.flag_type].join(''))].filter(fff => !!fff).join(" · ");
  }

  function renderFlag(flag: AdhImportFlag) {
    const rowData = flag.row_data || {};
    const isOpen = flag.status === "open";
    return <Paper key={flag.id} sx={{padding: 2, marginTop: 2}}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12}>
          <Typography variant="h6" sx={{display: 'inline-block'}}>{flagHeader(flag)}</Typography>
          <Chip size="small" label={t(["admin.adh_imports.flags.status.", flag.status].join(''))} color={chipColor(flag.status)} sx={{marginLeft: 1}} />
        </Grid>
      </Grid>
      {isOpen && <>
        {renderContextLine(t("admin.adh_imports.flags.raw.advice"), rowData.advice_raw)}
        {renderContextLine(t("admin.adh_imports.flags.raw.max"), rowData.max_raw)}
        {renderContextLine(t("admin.adh_imports.flags.raw.advice_type"), rowData.advice_type)}
        {renderContextLine(t("admin.adh_imports.flags.raw.remark"), rowData.remark)}
        {renderContextLine(t("admin.adh_imports.flags.raw.source"), rowData.source)}
      </>}
      {!isOpen && !!flag.resolution?.note && <Typography variant="caption" sx={{display: 'block', marginLeft: 2}}>{flag.resolution.note}</Typography>}
      {isOpen && flag.flag_type === "profile_metadata" && !!flag.adh_profile_id && <div>
        <Typography variant="caption" sx={{display: 'block', marginLeft: 2, marginTop: 1}}>
          <RouterLink to={"/adh_profile/" + flag.adh_profile_id}>{flag.adh_profile?.name || ["/adh_profile/", flag.adh_profile_id].join("")}</RouterLink>
        </Typography>
      </div>}
      {isOpen && renderOpenFlagForm(flag)}
    </Paper>;
  }

  return <Page
    title={t("admin.adh_imports.flags.title")}
    titleCrumbs={[
      {
        title: t("admin.menu.adh_imports"),
        path: "/adh_imports"
      }
    ]}
  >
    <Paper sx={{padding: 2}}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Typography variant="caption">{t("admin.adh_imports.flags.import_status")}: {t(["admin.adh_imports.status.", importObject?.status].join(''))}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption">
            {t("admin.adh_imports.flags.counts.total")}: {counts?.total || 0}
            <Box component="span" sx={{marginLeft: 2}}>{t("admin.adh_imports.flags.status.open")}: {counts?.open || 0}</Box>
            <Box component="span" sx={{marginLeft: 2}}>{t("admin.adh_imports.flags.status.resolved")}: {counts?.resolved || 0}</Box>
            <Box component="span" sx={{marginLeft: 2}}>{t("admin.adh_imports.flags.status.skipped")}: {counts?.skipped || 0}</Box>
            <Box component="span" sx={{marginLeft: 2}}>{t("admin.adh_imports.flags.status.no_norm")}: {counts?.no_norm || 0}</Box>
          </Typography>
        </Grid>
      </Grid>
      <Divider sx={{marginTop: 2}} />
      <Grid container spacing={2} sx={{marginTop: 0}}>
        <Grid item xs={4}>
          <Select
            fullWidth
            displayEmpty
            caption={t("admin.adh_imports.flags.country.title")}
            id="flag_filter_country"
            value={countryId}
            setValue={(v) => {setCountryId(v.toString());}}
          >
            <MenuItem value="">{t("admin.adh_imports.flags.country.all")}</MenuItem>
            <MenuItem value={"1"}>{t("admin.adh_imports.flags.country.be")}</MenuItem>
            <MenuItem value={"2"}>{t("admin.adh_imports.flags.country.nl")}</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={4}>
          <Select
            fullWidth
            displayEmpty
            caption={t("admin.adh_imports.flags.type.title")}
            id="flag_filter_type"
            value={flagType}
            setValue={(v) => {setFlagType(v.toString());}}
          >
            <MenuItem value="">{t("admin.adh_imports.flags.type.all")}</MenuItem>
            {FLAG_TYPES.map(ft => <MenuItem key={ft} value={ft}>{t(["admin.adh_imports.flags.type.", ft].join(''))}</MenuItem>)}
          </Select>
        </Grid>
        <Grid item xs={4}>
          <Select
            fullWidth
            displayEmpty
            caption={t("admin.adh_imports.flags.status.title")}
            id="flag_filter_status"
            value={flagStatus}
            setValue={(v) => {setFlagStatus(v.toString());}}
          >
            <MenuItem value={"open"}>{t("admin.adh_imports.flags.status.open")}</MenuItem>
            <MenuItem value={"resolved"}>{t("admin.adh_imports.flags.status.resolved")}</MenuItem>
            <MenuItem value={"skipped"}>{t("admin.adh_imports.flags.status.skipped")}</MenuItem>
          </Select>
        </Grid>
      </Grid>
    </Paper>

    {flagsLoading && <CircularProgress sx={{display: 'block', margin: 'auto', marginTop: 4}} />}
    {!flagsLoading && (flags || []).map(flag => renderFlag(flag))}

    <SnackSaved open={savedOpen} setOpen={setSavedOpen} />
  </Page>;
}
